import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import type { BlogAnalyticsDashboard } from '../../../types';

interface AnalyticsViewProps {
  analytics: BlogAnalyticsDashboard | null;
  loading: boolean;
  onPeriodChange: (from?: string, to?: string) => void;
}

export function AnalyticsView({ analytics, loading, onPeriodChange }: AnalyticsViewProps) {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    const to = new Date().toISOString();
    const from = new Date();
    if (period === '7d') from.setDate(from.getDate() - 7);
    else if (period === '30d') from.setDate(from.getDate() - 30);
    else from.setDate(from.getDate() - 90);
    onPeriodChange(from.toISOString(), to);
  }, [period, onPeriodChange]);

  if (loading) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Carregando analytics...
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Nenhum dado disponível.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex gap-2">
        {(['7d', '30d', '90d'] as const).map((p) => (
          <Button
            key={p}
            variant={period === p ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriod(p)}
          >
            {p === '7d' ? '7 dias' : p === '30d' ? '30 dias' : '90 dias'}
          </Button>
        ))}
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total de Views"
          value={analytics.totalViews.toLocaleString('pt-BR')}
        />
        <MetricCard
          label="Tempo Médio de Leitura"
          value={`${Math.round(analytics.avgReadTimeSec / 60)}min`}
        />
        <MetricCard
          label="Scroll Médio"
          value={`${analytics.avgScrollDepth}%`}
        />
        <MetricCard
          label="Cliques em CTA"
          value={analytics.totalCtaClicks.toLocaleString('pt-BR')}
        />
      </div>

      {/* Views over time chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Views ao Longo do Tempo</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.viewsOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 12 }} />
              <YAxis stroke="#666" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="views"
                stroke="#50E3C2"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top posts table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Posts Mais Populares</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead className="text-right">Tempo Leitura</TableHead>
                <TableHead className="text-right">Scroll</TableHead>
                <TableHead className="text-right">CTA %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.topPosts.map((post) => (
                <TableRow key={post.postId}>
                  <TableCell className="font-medium">{post.title}</TableCell>
                  <TableCell className="text-right">
                    {post.views.toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    {Math.round(post.avgReadTime / 60)}min
                  </TableCell>
                  <TableCell className="text-right">{post.scrollDepth}%</TableCell>
                  <TableCell className="text-right">
                    {(post.ctaRate * 100).toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Traffic sources */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Fontes de Tráfego</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={analytics.trafficSources} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis type="number" stroke="#666" tick={{ fontSize: 12 }} />
              <YAxis
                dataKey="source"
                type="category"
                stroke="#666"
                width={100}
                tick={{ fontSize: 12 }}
              />
              <Tooltip />
              <Bar dataKey="views" fill="#50E3C2" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}
